import React, { useEffect, useMemo, useState } from 'react';
import {
  SimpleGrid, Box, Text, HStack, VStack, Icon, Spinner, Badge, Divider,
  Stat, StatLabel, StatNumber, StatHelpText, Table, Thead, Tbody, Tr, Th, Td,
  Input, Button, Wrap, WrapItem, Flex,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
} from '@chakra-ui/react';
import {
  MdDashboard, MdEmojiEvents, MdPerson, MdCampaign, MdAssignment,
  MdInventory2, MdSportsSoccer, MdFilterAlt, MdClose,
} from 'react-icons/md';
import { IconType } from 'react-icons';
import deportesApi, {
  Torneo, Encuentro, Inscripcion, Deportista, Convocatoria, Articulo,
} from '../../../api/deportesApi';
import { PaginaDeportes, ROJO, ROJO_HOVER, fmtFecha, nombreCompleto } from '../_shared';

type EncuentroExt = Encuentro & { torneoNombre?: string };

const ESTATUS_ACTIVO = (t: Torneo) => t.estatus === 'Programado' || t.estatus === 'En proceso';

// ─────────── Tarjeta KPI interactiva (drill-down) ───────────
const TarjetaKPI: React.FC<{
  icon: IconType;
  label: string;
  value: React.ReactNode;
  help: string;
  color: string;
  onClick: () => void;
}> = ({ icon, label, value, help, color, onClick }) => (
  <Box
    bg="white"
    borderRadius="lg"
    boxShadow="sm"
    p={4}
    borderLeft={`4px solid ${color}`}
    cursor="pointer"
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
    transition="all 0.15s ease"
    _hover={{ boxShadow: 'lg', transform: 'translateY(-3px)' }}
    _active={{ transform: 'translateY(-1px)' }}
  >
    <HStack justify="space-between">
      <Stat>
        <StatLabel color="gray.500" fontSize="xs">{label}</StatLabel>
        <StatNumber color={color}>{value}</StatNumber>
        <StatHelpText fontSize="xs" mb={0}>{help}</StatHelpText>
      </Stat>
      <Icon as={icon} boxSize={9} color={color} opacity={0.85} />
    </HStack>
  </Box>
);

// ─────────── Rango de fecha ───────────
const enRango = (v: string | null | undefined, desde: string, hasta: string): boolean => {
  if (!desde && !hasta) return true;
  if (!v) return false;
  const t = new Date(v).getTime();
  if (isNaN(t)) return false;
  if (desde && t < new Date(`${desde}T00:00:00`).getTime()) return false;
  if (hasta && t > new Date(`${hasta}T23:59:59`).getTime()) return false;
  return true;
};

type ModalKey = 'torneos' | 'deportistas' | 'proximos' | 'convocatorias' | 'inscripciones' | 'prestado';

const Dashboard: React.FC = () => {
  const [cargando, setCargando] = useState(true);

  // Datos crudos (se cargan una sola vez; el filtro de fecha es client-side)
  const [torneos, setTorneos] = useState<Torneo[]>([]);
  const [deportistas, setDeportistas] = useState<Deportista[]>([]);
  const [convocatorias, setConvocatorias] = useState<Convocatoria[]>([]);
  const [inscripciones, setInscripciones] = useState<Inscripcion[]>([]);
  const [articulos, setArticulos] = useState<Articulo[]>([]);
  const [encuentros, setEncuentros] = useState<EncuentroExt[]>([]);

  // Filtro de fecha: valores en edición vs. aplicados
  const [desdeInput, setDesdeInput] = useState('');
  const [hastaInput, setHastaInput] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  const [modal, setModal] = useState<ModalKey | null>(null);

  useEffect(() => {
    (async () => {
      setCargando(true);
      try {
        const [tds, dep, conv, insc, arts] = await Promise.all([
          deportesApi.listarTorneos().catch(() => []),
          deportesApi.listarDeportistas().catch(() => []),
          deportesApi.listarConvocatorias().catch(() => []),
          deportesApi.listarInscripciones({ estatus: 'Recibida' }).catch(() => []),
          deportesApi.listarArticulos().catch(() => []),
        ]);
        setTorneos(tds || []);
        setDeportistas(dep || []);
        setConvocatorias(conv || []);
        setInscripciones(insc || []);
        setArticulos(arts || []);

        // Encuentros de todos los torneos activos (para "próximos encuentros")
        const activos = (tds || []).filter(ESTATUS_ACTIVO);
        const listas = await Promise.all(activos.map((t) =>
          deportesApi.listarEncuentros(t.id)
            .then((es) => es.map((e) => ({ ...e, torneoNombre: t.nombre })))
            .catch(() => [] as EncuentroExt[])
        ));
        setEncuentros(listas.flat());
      } finally { setCargando(false); }
    })();
  }, []);

  const aplicar = () => { setDesde(desdeInput); setHasta(hastaInput); };
  const limpiar = () => { setDesdeInput(''); setHastaInput(''); setDesde(''); setHasta(''); };
  const hayFiltro = Boolean(desde || hasta);

  // ─────────── Cálculos derivados (recalculan al aplicar el rango) ───────────
  const torneosActivos = useMemo(
    () => torneos.filter((t) => ESTATUS_ACTIVO(t) && enRango(t.fechaInicio, desde, hasta)),
    [torneos, desde, hasta],
  );

  // Los deportistas no tienen fecha de registro: no se filtran por rango.
  const deportistasFilt = deportistas;

  const convocatoriasVigentes = useMemo(
    () => convocatorias.filter((c) => c.estatus === 'Vigente' && enRango(c.fechaPublicacion, desde, hasta)),
    [convocatorias, desde, hasta],
  );
  const convocatoriasPublicadas = useMemo(
    () => convocatorias.filter((c) => c.publicadaPortal && enRango(c.fechaPublicacion, desde, hasta)).length,
    [convocatorias, desde, hasta],
  );

  const inscripcionesRevisar = useMemo(
    () => inscripciones.filter((i) => (i.estatus || 'Recibida') === 'Recibida' && enRango(i.fecha, desde, hasta)),
    [inscripciones, desde, hasta],
  );

  // Artículos con unidades fuera de resguardo (prestadas)
  const articulosPrestados = useMemo(
    () => articulos
      .map((a) => ({ art: a, prestado: Math.max(0, (a.cantidad ?? 0) - (a.cantidadDisponible ?? 0)) }))
      .filter((x) => x.prestado > 0),
    [articulos],
  );
  const totalPrestado = useMemo(
    () => articulosPrestados.reduce((s, x) => s + x.prestado, 0),
    [articulosPrestados],
  );

  const proximosEncuentros = useMemo(() => {
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    return encuentros
      .filter((e) => e.estatus !== 'Finalizado' && e.estatus !== 'Cancelado' && e.fecha)
      .filter((e) => hayFiltro ? enRango(e.fecha, desde, hasta) : new Date(e.fecha!).getTime() >= hoy.getTime())
      .sort((a, b) => new Date(a.fecha!).getTime() - new Date(b.fecha!).getTime());
  }, [encuentros, desde, hasta, hayFiltro]);

  // ─────────── Configuración de cada modal (drill-down) ───────────
  const partido = (e: EncuentroExt) => `${e.equipoLocal?.nombre || '?'} vs ${e.equipoVisitante?.nombre || '?'}`;

  const modales: Record<ModalKey, { titulo: string; icon: IconType; head: string[]; filas: () => React.ReactNode[] }> = {
    torneos: {
      titulo: 'Torneos activos', icon: MdEmojiEvents,
      head: ['Nombre', 'Disciplina', 'Sede', 'Inicio', 'Estatus'],
      filas: () => torneosActivos.map((t) => (
        <Tr key={t.id}>
          <Td fontSize="xs">{t.nombre}</Td>
          <Td fontSize="xs">{t.disciplina?.nombre || '—'}</Td>
          <Td fontSize="xs">{t.localidad || t.sede || '—'}</Td>
          <Td fontSize="xs">{fmtFecha(t.fechaInicio)}</Td>
          <Td fontSize="xs"><Badge colorScheme={t.estatus === 'En proceso' ? 'yellow' : 'cyan'}>{t.estatus}</Badge></Td>
        </Tr>
      )),
    },
    deportistas: {
      titulo: 'Deportistas registrados', icon: MdPerson,
      head: ['Nombre', 'Disciplina', 'Categoría', 'Localidad', 'Estatus'],
      filas: () => deportistasFilt.map((d) => (
        <Tr key={d.id}>
          <Td fontSize="xs">{nombreCompleto(d)}</Td>
          <Td fontSize="xs">{d.disciplina?.nombre || '—'}</Td>
          <Td fontSize="xs">{d.categoria?.nombre || '—'}</Td>
          <Td fontSize="xs">{d.localidad || '—'}</Td>
          <Td fontSize="xs">{d.estatus || '—'}</Td>
        </Tr>
      )),
    },
    proximos: {
      titulo: 'Próximos encuentros', icon: MdSportsSoccer,
      head: ['Fecha', 'Torneo', 'Partido', 'Sede', 'Estatus'],
      filas: () => proximosEncuentros.map((e) => (
        <Tr key={e.id}>
          <Td fontSize="xs">{fmtFecha(e.fecha)} {e.hora || ''}</Td>
          <Td fontSize="xs">{e.torneoNombre || '—'}</Td>
          <Td fontSize="xs">{partido(e)}</Td>
          <Td fontSize="xs">{e.sede || '—'}</Td>
          <Td fontSize="xs">{e.estatus || '—'}</Td>
        </Tr>
      )),
    },
    convocatorias: {
      titulo: 'Convocatorias vigentes', icon: MdCampaign,
      head: ['Folio', 'Nombre', 'Publicación', 'Cierre', 'Portal'],
      filas: () => convocatoriasVigentes.map((c) => (
        <Tr key={c.id}>
          <Td fontSize="xs">{c.folio || '—'}</Td>
          <Td fontSize="xs">{c.nombre}</Td>
          <Td fontSize="xs">{fmtFecha(c.fechaPublicacion)}</Td>
          <Td fontSize="xs">{fmtFecha(c.fechaCierre)}</Td>
          <Td fontSize="xs">
            <Badge colorScheme={c.publicadaPortal ? 'green' : 'gray'}>{c.publicadaPortal ? 'Publicada' : 'No'}</Badge>
          </Td>
        </Tr>
      )),
    },
    inscripciones: {
      titulo: 'Inscripciones por revisar', icon: MdAssignment,
      head: ['Folio', 'Nombre', 'Convocatoria', 'Fecha', 'Estatus'],
      filas: () => inscripcionesRevisar.map((i) => (
        <Tr key={i.id}>
          <Td fontSize="xs">{i.folio || '—'}</Td>
          <Td fontSize="xs">{nombreCompleto(i)}</Td>
          <Td fontSize="xs">{i.convocatoria?.nombre || '—'}</Td>
          <Td fontSize="xs">{fmtFecha(i.fecha)}</Td>
          <Td fontSize="xs"><Badge colorScheme="orange">{i.estatus || 'Recibida'}</Badge></Td>
        </Tr>
      )),
    },
    prestado: {
      titulo: 'Inventario prestado', icon: MdInventory2,
      head: ['N° inventario', 'Artículo', 'Cantidad', 'Disponible', 'Prestado'],
      filas: () => articulosPrestados.map(({ art, prestado }) => (
        <Tr key={art.id}>
          <Td fontSize="xs">{art.numeroInventario}</Td>
          <Td fontSize="xs">{art.nombre}</Td>
          <Td fontSize="xs" isNumeric>{art.cantidad ?? 0}</Td>
          <Td fontSize="xs" isNumeric>{art.cantidadDisponible ?? 0}</Td>
          <Td fontSize="xs" isNumeric fontWeight="bold" color={ROJO}>{prestado}</Td>
        </Tr>
      )),
    },
  };

  if (cargando) {
    return (
      <PaginaDeportes icon={MdDashboard} titulo="Panel de Deportes" subtitulo="Resumen general del módulo de deportes">
        <VStack py={12}><Spinner color={ROJO} size="lg" /><Text color="gray.500">Cargando indicadores...</Text></VStack>
      </PaginaDeportes>
    );
  }

  const cfg = modal ? modales[modal] : null;

  return (
    <PaginaDeportes icon={MdDashboard} titulo="Panel de Deportes" subtitulo="Resumen general del módulo de deportes">
      {/* ─────────── Filtro de fecha ─────────── */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" p={4} borderTop={`3px solid ${ROJO}`}>
        <Wrap spacing={4} align="flex-end">
          <WrapItem>
            <HStack spacing={2} color={ROJO}>
              <Icon as={MdFilterAlt} />
              <Text fontWeight="bold" fontSize="sm">Filtrar por fecha</Text>
            </HStack>
          </WrapItem>
          <WrapItem>
            <Box>
              <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={1}>Desde</Text>
              <Input
                type="date" size="sm" value={desdeInput} maxW="170px"
                onChange={(e) => setDesdeInput(e.target.value)}
                focusBorderColor={ROJO}
              />
            </Box>
          </WrapItem>
          <WrapItem>
            <Box>
              <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={1}>Hasta</Text>
              <Input
                type="date" size="sm" value={hastaInput} maxW="170px"
                onChange={(e) => setHastaInput(e.target.value)}
                focusBorderColor={ROJO}
              />
            </Box>
          </WrapItem>
          <WrapItem>
            <HStack spacing={2}>
              <Button
                size="sm" color="white" bg={ROJO} _hover={{ bg: ROJO_HOVER }}
                leftIcon={<MdFilterAlt />} onClick={aplicar}
              >
                Aplicar
              </Button>
              <Button
                size="sm" variant="outline" color={ROJO} borderColor={ROJO}
                _hover={{ bg: 'gray.50' }} leftIcon={<MdClose />} onClick={limpiar}
                isDisabled={!hayFiltro && !desdeInput && !hastaInput}
              >
                Limpiar
              </Button>
            </HStack>
          </WrapItem>
          {hayFiltro && (
            <WrapItem>
              <Badge bg={ROJO} color="white" px={2} py={1} borderRadius="md" fontSize="2xs">
                Rango: {desde ? fmtFecha(desde) : '…'} – {hasta ? fmtFecha(hasta) : '…'}
              </Badge>
            </WrapItem>
          )}
        </Wrap>
      </Box>

      {/* ─────────── KPIs interactivos ─────────── */}
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4}>
        <TarjetaKPI icon={MdEmojiEvents} label="Torneos activos" value={torneosActivos.length}
          help={hayFiltro ? 'En el rango seleccionado' : `${torneos.length} en total`} color={ROJO}
          onClick={() => setModal('torneos')} />
        <TarjetaKPI icon={MdPerson} label="Deportistas registrados" value={deportistasFilt.length}
          help="Padrón deportivo" color="#2B6CB0" onClick={() => setModal('deportistas')} />
        <TarjetaKPI icon={MdCampaign} label="Convocatorias vigentes" value={convocatoriasVigentes.length}
          help={`${convocatoriasPublicadas} publicadas en portal`} color="#2F855A"
          onClick={() => setModal('convocatorias')} />
        <TarjetaKPI icon={MdAssignment} label="Inscripciones por revisar" value={inscripcionesRevisar.length}
          help="Estatus: Recibida" color="#B7791F" onClick={() => setModal('inscripciones')} />
        <TarjetaKPI icon={MdInventory2} label="Inventario prestado" value={totalPrestado}
          help="Unidades fuera de resguardo" color="#6B46C1" onClick={() => setModal('prestado')} />
        <TarjetaKPI icon={MdSportsSoccer} label="Próximos encuentros" value={proximosEncuentros.length}
          help={hayFiltro ? 'En el rango seleccionado' : 'En torneos activos'} color="#C05621"
          onClick={() => setModal('proximos')} />
      </SimpleGrid>

      {/* ─────────── Paneles resumen (reflejan el filtro) ─────────── */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} mt={2}>
        {/* Próximos encuentros */}
        <Box bg="white" borderRadius="lg" boxShadow="sm" p={4}>
          <HStack mb={2}><Icon as={MdSportsSoccer} color={ROJO} /><Text fontWeight="bold" color={ROJO}>Próximos encuentros</Text></HStack>
          <Divider mb={2} />
          {proximosEncuentros.length === 0 ? (
            <Text fontSize="sm" color="gray.400" py={4} textAlign="center">Sin encuentros programados</Text>
          ) : (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead><Tr><Th>Fecha</Th><Th>Torneo</Th><Th>Partido</Th></Tr></Thead>
                <Tbody>
                  {proximosEncuentros.slice(0, 8).map((e) => (
                    <Tr key={e.id}>
                      <Td fontSize="xs">{fmtFecha(e.fecha)} {e.hora || ''}</Td>
                      <Td fontSize="xs"><Text noOfLines={1} maxW="140px">{e.torneoNombre}</Text></Td>
                      <Td fontSize="xs">{partido(e)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>

        {/* Torneos activos */}
        <Box bg="white" borderRadius="lg" boxShadow="sm" p={4}>
          <HStack mb={2}><Icon as={MdEmojiEvents} color={ROJO} /><Text fontWeight="bold" color={ROJO}>Torneos activos</Text></HStack>
          <Divider mb={2} />
          {torneosActivos.length === 0 ? (
            <Text fontSize="sm" color="gray.400" py={4} textAlign="center">Sin torneos activos</Text>
          ) : (
            <VStack align="stretch" spacing={2}>
              {torneosActivos.slice(0, 8).map((t) => (
                <HStack key={t.id} justify="space-between" borderBottomWidth="1px" pb={1}>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" noOfLines={1}>{t.nombre}</Text>
                    <Text fontSize="2xs" color="gray.500">{t.disciplina?.nombre} · {t.localidad || 'Sin sede'}</Text>
                  </Box>
                  <Badge colorScheme={t.estatus === 'En proceso' ? 'yellow' : 'cyan'}>{t.estatus}</Badge>
                </HStack>
              ))}
            </VStack>
          )}
        </Box>
      </SimpleGrid>

      {/* ─────────── Modal drill-down ─────────── */}
      <Modal isOpen={!!cfg} onClose={() => setModal(null)} size="4xl" scrollBehavior="inside" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader bg={ROJO} color="white" borderTopRadius="md" py={3}>
            <HStack spacing={2}>
              {cfg && <Icon as={cfg.icon} />}
              <Text fontSize="md">{cfg?.titulo}</Text>
              {cfg && (
                <Badge bg="white" color={ROJO} borderRadius="full" px={2}>
                  {cfg.filas().length}
                </Badge>
              )}
              {hayFiltro && (
                <Text fontSize="xs" opacity={0.85} fontWeight="normal">
                  ({desde ? fmtFecha(desde) : '…'} – {hasta ? fmtFecha(hasta) : '…'})
                </Text>
              )}
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py={4}>
            {(() => {
              if (!cfg) return null;
              const filas = cfg.filas();
              if (filas.length === 0) {
                return (
                  <Flex direction="column" align="center" py={10} color="gray.400">
                    <Icon as={cfg.icon} boxSize={10} mb={2} />
                    <Text fontSize="sm">Sin registros{hayFiltro ? ' en el rango seleccionado' : ''}</Text>
                  </Flex>
                );
              }
              return (
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead bg={ROJO}>
                      <Tr>
                        {cfg.head.map((h) => (
                          <Th key={h} color="white" borderColor={ROJO}>{h}</Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>{filas}</Tbody>
                  </Table>
                </Box>
              );
            })()}
          </ModalBody>
        </ModalContent>
      </Modal>
    </PaginaDeportes>
  );
};

export default Dashboard;
